import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "./useNotification";
import { BrandingService } from "@root/services/admin/branding";
import { useBrandingContext } from "@root/context/branding-context";

export const useAdminBranding = () => {
  const { t } = useTranslation("common");
  const { showNotification } = useNotification();
  const { refreshBranding, isFeatureEnabled } = useBrandingContext();
  const brandingService = useMemo(() => new BrandingService(), []);

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<IBrandingConfig | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Section-specific save loading states
  const [savingLogo, setSavingLogo] = useState(false);
  const [savingColors, setSavingColors] = useState(false);
  const [savingTypography, setSavingTypography] = useState(false);
  const [savingLoginPage, setSavingLoginPage] = useState(false);
  const [savingDomain, setSavingDomain] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingMetadata, setSavingMetadata] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  const fetchConfig = useCallback(async () => {
    if (!config?.organizationId) return;
    setLoading(true);
    try {
      const response = await brandingService.fetchBrandingConfig(
        baseUrl,
        config.organizationId
      );
      if (!response?.error && response?.data) {
        setConfig(response.data);
      }
    } catch (err: any) {
      showNotification({
        type: "error",
        message: t("admin.branding.fetchError"),
      });
    } finally {
      setLoading(false);
    }
  }, [brandingService, baseUrl, config?.organizationId, showNotification, t]);

  const handleUploadLogo = useCallback(
    async (formData: FormData) => {
      if (!config?.organizationId) return;
      setSavingLogo(true);
      try {
        const response = await brandingService.uploadLogo(
          baseUrl,
          config.organizationId,
          formData
        );
        if (!response?.error) {
          showNotification({
            type: "success",
            message: t("admin.branding.logo.uploadSuccess"),
          });
          await refreshBranding();
          await fetchConfig();
        } else {
          showNotification({
            type: "error",
            message: t("admin.branding.logo.uploadError"),
          });
        }
      } catch (err: any) {
        showNotification({
          type: "error",
          message: t("admin.branding.logo.uploadError"),
        });
      } finally {
        setSavingLogo(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, refreshBranding, fetchConfig]
  );

  const handleDeleteLogo = useCallback(
    async (logoType: string) => {
      if (!config?.organizationId) return;
      setSavingLogo(true);
      try {
        const response = await brandingService.deleteLogo(
          baseUrl,
          config.organizationId,
          logoType
        );
        if (!response?.error) {
          showNotification({
            type: "success",
            message: t("admin.branding.logo.removeSuccess"),
          });
          await refreshBranding();
          await fetchConfig();
        } else {
          showNotification({
            type: "error",
            message: t("admin.branding.logo.removeError"),
          });
        }
      } catch (err: any) {
        showNotification({
          type: "error",
          message: t("admin.branding.logo.removeError"),
        });
      } finally {
        setSavingLogo(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, refreshBranding, fetchConfig]
  );

  const handleUpdateColors = useCallback(
    async (colors: IBrandingColors) => {
      if (!config?.organizationId) return;
      setSavingColors(true);
      try {
        const response = await brandingService.updateColors(
          baseUrl,
          config.organizationId,
          colors
        );
        if (!response?.error) {
          showNotification({
            type: "success",
            message: t("admin.branding.colors.saveSuccess"),
          });
          await refreshBranding();
          await fetchConfig();
        } else {
          showNotification({
            type: "error",
            message: t("admin.branding.colors.saveError"),
          });
        }
      } catch (err: any) {
        showNotification({
          type: "error",
          message: t("admin.branding.colors.saveError"),
        });
      } finally {
        setSavingColors(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, refreshBranding, fetchConfig]
  );

  const handleUpdateTypography = useCallback(
    async (typography: IBrandingTypography) => {
      if (!config?.organizationId) return;
      setSavingTypography(true);
      try {
        const response = await brandingService.updateTypography(
          baseUrl,
          config.organizationId,
          typography
        );
        if (!response?.error) {
          showNotification({
            type: "success",
            message: t("admin.branding.typography.saveSuccess"),
          });
          await refreshBranding();
          await fetchConfig();
        } else {
          showNotification({
            type: "error",
            message: t("admin.branding.typography.saveError"),
          });
        }
      } catch (err: any) {
        showNotification({
          type: "error",
          message: t("admin.branding.typography.saveError"),
        });
      } finally {
        setSavingTypography(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, refreshBranding, fetchConfig]
  );

  const handleUpdateLoginPage = useCallback(
    async (loginPage: IBrandingLoginPage) => {
      if (!config?.organizationId) return;
      setSavingLoginPage(true);
      try {
        const response = await brandingService.updateLoginPage(
          baseUrl,
          config.organizationId,
          loginPage
        );
        if (!response?.error) {
          showNotification({
            type: "success",
            message: t("admin.branding.save"),
          });
          await refreshBranding();
          await fetchConfig();
        }
      } catch (err: any) {
        showNotification({ type: "error", message: t("admin.branding.fetchError") });
      } finally {
        setSavingLoginPage(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, refreshBranding, fetchConfig]
  );

  const handleUpdateEmailBranding = useCallback(
    async (emailBranding: IBrandingEmailBranding) => {
      if (!config?.organizationId) return;
      setSavingEmail(true);
      try {
        const response = await brandingService.updateEmailBranding(
          baseUrl,
          config.organizationId,
          emailBranding
        );
        if (!response?.error) {
          showNotification({
            type: "success",
            message: t("admin.branding.email.saveSuccess"),
          });
          await refreshBranding();
          await fetchConfig();
        } else {
          showNotification({
            type: "error",
            message: t("admin.branding.email.saveError"),
          });
        }
      } catch (err: any) {
        showNotification({
          type: "error",
          message: t("admin.branding.email.saveError"),
        });
      } finally {
        setSavingEmail(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, refreshBranding, fetchConfig]
  );

  const handleUpdateMetadata = useCallback(
    async (metadata: IBrandingMetadata) => {
      if (!config?.organizationId) return;
      setSavingMetadata(true);
      try {
        const response = await brandingService.updateMetadata(
          baseUrl,
          config.organizationId,
          metadata
        );
        if (!response?.error) {
          showNotification({ type: "success", message: t("admin.branding.save") });
          await refreshBranding();
          await fetchConfig();
        }
      } catch (err: any) {
        showNotification({ type: "error", message: t("admin.branding.fetchError") });
      } finally {
        setSavingMetadata(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, refreshBranding, fetchConfig]
  );

  const handleAddDomain = useCallback(
    async (domain: string) => {
      if (!config?.organizationId) return;
      setSavingDomain(true);
      try {
        const response = await brandingService.addCustomDomain(
          baseUrl,
          config.organizationId,
          { domain }
        );
        if (!response?.error) {
          showNotification({ type: "success", message: t("admin.branding.save") });
          await fetchConfig();
        }
      } catch (err: any) {
        showNotification({ type: "error", message: t("admin.branding.fetchError") });
      } finally {
        setSavingDomain(false);
      }
    },
    [brandingService, baseUrl, config?.organizationId, showNotification, t, fetchConfig]
  );

  const handleVerifyDomain = useCallback(async () => {
    if (!config?.organizationId) return;
    setSavingDomain(true);
    try {
      const response = await brandingService.verifyCustomDomain(
        baseUrl,
        config.organizationId
      );
      if (!response?.error) {
        showNotification({
          type: "success",
          message: t("admin.branding.domain.verified"),
        });
        await fetchConfig();
      }
    } catch (err: any) {
      showNotification({
        type: "error",
        message: t("admin.branding.domain.failed"),
      });
    } finally {
      setSavingDomain(false);
    }
  }, [brandingService, baseUrl, config?.organizationId, showNotification, t, fetchConfig]);

  const handleDeleteDomain = useCallback(async () => {
    if (!config?.organizationId) return;
    setSavingDomain(true);
    try {
      const response = await brandingService.deleteCustomDomain(
        baseUrl,
        config.organizationId
      );
      if (!response?.error) {
        showNotification({ type: "success", message: t("admin.branding.save") });
        await fetchConfig();
      }
    } catch (err: any) {
      showNotification({ type: "error", message: t("admin.branding.fetchError") });
    } finally {
      setSavingDomain(false);
    }
  }, [brandingService, baseUrl, config?.organizationId, showNotification, t, fetchConfig]);

  // Initialize config from context branding
  const { branding, organizationId } = useBrandingContext();
  useEffect(() => {
    if (branding && !config) {
      setConfig(branding);
    }
  }, [branding, config]);

  const tabs = [
    t("admin.branding.tabs.logoImages"),
    t("admin.branding.tabs.colors"),
    t("admin.branding.tabs.typography"),
    t("admin.branding.tabs.customDomain"),
    t("admin.branding.tabs.emailTemplates"),
  ];

  return {
    loading,
    config,
    activeTab,
    setActiveTab,
    tabs,
    handleUploadLogo,
    handleDeleteLogo,
    savingLogo,
    handleUpdateColors,
    savingColors,
    handleUpdateTypography,
    savingTypography,
    handleUpdateLoginPage,
    savingLoginPage,
    handleUpdateEmailBranding,
    savingEmail,
    handleUpdateMetadata,
    savingMetadata,
    handleAddDomain,
    handleVerifyDomain,
    handleDeleteDomain,
    savingDomain,
    isFeatureEnabled,
    fetchConfig,
  };
};
